import { useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Upload, X, FileText, Trash2, Save, Loader2 } from "lucide-react";

// Convert a chosen File (image or PDF) into a data URI suitable for the
// /receipts/extract-parts endpoint.
function fileToDataUri(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Couldn't read file"));
        reader.readAsDataURL(file);
    });
}

const STAGES = { PICK: "pick", BUSY: "busy", REVIEW: "review", SAVING: "saving" };

/**
 * Import parts from a marine receipt (PDF or photo).
 *
 * Flow:
 *  1. PICK    — user uploads a PDF/photo
 *  2. BUSY    — backend AI extracts vendor, date, total + line items
 *  3. REVIEW  — editable table; user toggles which lines to add, can rename/edit
 *  4. SAVING  — bulk-creates Parts entries + a single Costs entry tied to the receipt
 */
export default function ImportPartsModal({ vesselId, category = "Parts", defaultCurrency = "USD", hasCostsTab = true, onClose, onImported }) {
    const [stage, setStage] = useState(STAGES.PICK);
    const [extracted, setExtracted] = useState(null); // raw response
    const [rows, setRows] = useState([]); // editable {description, oem_number, quantity, unit_amount, line_total, _enabled, _key}
    const [receipt, setReceipt] = useState({ vendor: "", date: "", currency: defaultCurrency, total: "" });
    const [dataUri, setDataUri] = useState(null); // kept so we can attach the receipt as a photo
    const [filename, setFilename] = useState("");
    const [createCostEntry, setCreateCostEntry] = useState(true);
    const [err, setErr] = useState("");
    const fileRef = useRef(null);

    const pickFile = () => fileRef.current?.click();

    const handleFile = async (file) => {
        if (!file) return;
        setErr("");
        // Client-side guard — backend caps at 8 MB raw bytes, so anything
        // over ~10 MB will base64-balloon past the limit. Stop early.
        if (file.size > 10 * 1024 * 1024) {
            setErr(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Use a PDF under 10 MB.`);
            return;
        }
        try {
            const duri = await fileToDataUri(file);
            setDataUri(duri);
            setFilename(file.name);
            setStage(STAGES.BUSY);
            const { data } = await api.post("/receipts/extract-parts", { data_uri: duri });
            if (!data.ok) {
                setErr(data.note || "Couldn't read this receipt.");
                setStage(STAGES.PICK);
                return;
            }
            setExtracted(data);
            setReceipt({
                vendor: data.vendor || "",
                date: data.date || "",
                currency: (data.currency || defaultCurrency).toUpperCase(),
                total: data.total != null ? String(data.total) : "",
            });
            setRows(
                (data.line_items || []).map((it, i) => ({
                    _key: `r${i}`,
                    _enabled: true,
                    description: it.description || "",
                    oem_number: it.oem_number || "",
                    quantity: it.quantity != null ? String(it.quantity) : "",
                    unit_amount: it.unit_amount != null ? String(it.unit_amount) : "",
                    line_total: it.line_total != null ? String(it.line_total) : "",
                })),
            );
            setStage(STAGES.REVIEW);
            if (!data.line_items || data.line_items.length === 0) {
                setErr("AI didn't find any line items on this receipt. Try a clearer photo or add parts manually.");
            }
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't read file.");
            setStage(STAGES.PICK);
        }
    };

    const updateRow = (key, patch) => {
        setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));
    };
    const removeRow = (key) => setRows((rs) => rs.filter((r) => r._key !== key));

    const enabledCount = rows.filter((r) => r._enabled && r.description.trim()).length;

    const save = async () => {
        setErr("");
        setStage(STAGES.SAVING);
        const ccy = (receipt.currency || defaultCurrency).toUpperCase();
        const toSave = rows.filter((r) => r._enabled && r.description.trim());
        if (toSave.length === 0) {
            setErr("Nothing to save — enable at least one row.");
            setStage(STAGES.REVIEW);
            return;
        }

        try {
            // 1. Create each Parts entry (reference category — no date, no amount required)
            for (const r of toSave) {
                const qty = parseFloat(r.quantity);
                const unit = parseFloat(r.unit_amount);
                const total = parseFloat(r.line_total);
                const amount = Number.isFinite(total) ? total : Number.isFinite(unit) ? unit : null;
                await api.post(`/vessels/${vesselId}/entries`, {
                    category,
                    date: null,
                    title: r.description.trim(),
                    amount: amount != null ? amount : null,
                    currency: amount != null ? ccy : null,
                    quantity: Number.isFinite(qty) ? qty : null,
                    unit: null,
                    vendor: receipt.vendor || null,
                    notes: Number.isFinite(unit) && Number.isFinite(qty)
                        ? `Unit: ${unit.toFixed(2)} ${ccy} · Qty: ${qty}`
                        : null,
                    oem_number: r.oem_number?.trim() || null,
                    aftermarket_number: null,
                    custom_data: {},
                });
            }

            // 2. (Optional) one Costs entry for the receipt total, with the
            //    PDF attached as a photo so the receipt itself is filed.
            if (createCostEntry && hasCostsTab && receipt.total) {
                const totalN = parseFloat(receipt.total);
                if (Number.isFinite(totalN) && totalN > 0) {
                    await api.post(`/vessels/${vesselId}/entries`, {
                        category: "Costs",
                        date: receipt.date || null,
                        title: `${receipt.vendor || "Parts receipt"} — ${toSave.length} part${toSave.length === 1 ? "" : "s"}`,
                        amount: totalN,
                        currency: ccy,
                        vendor: receipt.vendor || null,
                        notes: `Imported via AI from ${filename || "receipt"}`,
                        photos: dataUri ? [{ data: dataUri, filename: filename || "receipt" }] : null,
                        custom_data: {},
                    });
                }
            }

            onImported?.({ added: toSave.length, createdCostEntry: createCostEntry && hasCostsTab && !!receipt.total });
        } catch (e) {
            setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message || "Couldn't save parts.");
            setStage(STAGES.REVIEW);
        }
    };

    return (
        <div
            data-testid="import-parts-modal"
            className="bm-modal-overlay"
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "grid", placeItems: "center", padding: 20, zIndex: 100 }}
            onClick={stage === STAGES.SAVING ? undefined : onClose}
        >
            <div
                className="bm-card"
                style={{ padding: 28, maxWidth: 820, width: "100%", maxHeight: "92vh", overflow: "auto" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <h3 className="bm-serif" style={{ fontSize: 24, margin: 0, fontWeight: 500 }}>
                        <Sparkles size={18} color="var(--bm-gold)" style={{ verticalAlign: "-3px", marginRight: 6 }} />
                        Import parts from receipt
                    </h3>
                    <button onClick={onClose} className="bm-btn bm-btn-ghost" style={{ padding: 6 }} aria-label="Close" disabled={stage === STAGES.SAVING}>
                        <X size={16} />
                    </button>
                </div>

                {/* PICK */}
                {stage === STAGES.PICK && (
                    <div data-testid="import-stage-pick">
                        <p style={{ color: "var(--bm-text-muted)", fontSize: 14, lineHeight: 1.6 }}>
                            Upload a parts receipt (PDF or photo). The AI reads every line item, then you confirm which ones to add to your catalogue.
                            Optionally, we'll also file the receipt as a Costs entry so your books balance.
                        </p>
                        <input
                            data-testid="import-parts-file"
                            ref={fileRef}
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => handleFile(e.target.files?.[0])}
                            style={{ display: "none" }}
                        />
                        <button
                            data-testid="import-parts-upload-btn"
                            className="bm-btn bm-btn-gold"
                            onClick={pickFile}
                            style={{ marginTop: 18, padding: "14px 24px", fontSize: 15 }}
                        >
                            <Upload size={16} /> Choose a PDF or photo
                        </button>
                        {err && <div data-testid="import-parts-err" style={{ color: "var(--bm-bad)", marginTop: 14, fontSize: 13 }}>{err}</div>}
                    </div>
                )}

                {/* BUSY — AI is reading */}
                {stage === STAGES.BUSY && (
                    <div data-testid="import-stage-busy" style={{ padding: "40px 20px", textAlign: "center", color: "var(--bm-text-muted)" }}>
                        <Loader2 size={28} color="var(--bm-gold)" style={{ animation: "spin 1.4s linear infinite", marginBottom: 14 }} />
                        <div style={{ fontSize: 14 }}>Reading {filename || "your receipt"}…</div>
                        <div style={{ fontSize: 12, marginTop: 4, color: "var(--bm-text-dim)" }}>This can take 20–40 seconds for big receipts.</div>
                        <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
                    </div>
                )}

                {/* REVIEW — editable preview */}
                {stage === STAGES.REVIEW && (
                    <div data-testid="import-stage-review">
                        <div className="bm-card" style={{ padding: 16, marginBottom: 16, background: "rgba(212,181,114,0.06)", border: "1px solid rgba(212,181,114,0.25)" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, fontSize: 13 }}>
                                <div>
                                    <label className="bm-label">Vendor</label>
                                    <input data-testid="import-vendor-input" className="bm-input" value={receipt.vendor} onChange={(e) => setReceipt({ ...receipt, vendor: e.target.value })} />
                                </div>
                                <div>
                                    <label className="bm-label">Date</label>
                                    <input data-testid="import-date-input" className="bm-input" type="date" value={receipt.date} onChange={(e) => setReceipt({ ...receipt, date: e.target.value })} />
                                </div>
                                <div>
                                    <label className="bm-label">Total ({receipt.currency || defaultCurrency})</label>
                                    <input data-testid="import-total-input" className="bm-input" type="number" step="0.01" value={receipt.total} onChange={(e) => setReceipt({ ...receipt, total: e.target.value })} />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: 8, fontSize: 13, color: "var(--bm-text-muted)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                            <span>
                                <strong style={{ color: "var(--bm-text)" }}>{enabledCount}</strong> of {rows.length} line items selected for import.
                            </span>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button data-testid="import-select-all-btn" type="button" className="bm-btn bm-btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setRows((rs) => rs.map((r) => ({ ...r, _enabled: true })))}>Select all</button>
                                <button data-testid="import-select-none-btn" type="button" className="bm-btn bm-btn-ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setRows((rs) => rs.map((r) => ({ ...r, _enabled: false })))}>Select none</button>
                            </div>
                        </div>

                        <div className="bm-card" style={{ overflow: "hidden", marginBottom: 14 }}>
                            {/* Desktop view — table */}
                            <div className="bm-import-table" style={{ overflowX: "auto" }}>
                                <table className="bm-table" style={{ fontSize: 13 }}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 32 }}></th>
                                            <th>Description</th>
                                            <th>OEM #</th>
                                            <th style={{ width: 60 }}>Qty</th>
                                            <th style={{ width: 90 }}>Unit ({receipt.currency || defaultCurrency})</th>
                                            <th style={{ width: 90 }}>Line ({receipt.currency || defaultCurrency})</th>
                                            <th style={{ width: 36 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.length === 0 && (
                                            <tr><td colSpan={7} style={{ textAlign: "center", padding: 22, color: "var(--bm-text-muted)" }}>No line items found.</td></tr>
                                        )}
                                        {rows.map((r) => (
                                            <tr key={r._key} data-testid={`import-row-${r._key}`} style={{ opacity: r._enabled ? 1 : 0.45 }}>
                                                <td>
                                                    <input data-testid={`import-row-${r._key}-toggle`} type="checkbox" checked={r._enabled} onChange={(e) => updateRow(r._key, { _enabled: e.target.checked })} />
                                                </td>
                                                <td><input data-testid={`import-row-${r._key}-desc`} className="bm-input" style={{ padding: "4px 8px", fontSize: 13 }} value={r.description} onChange={(e) => updateRow(r._key, { description: e.target.value })} /></td>
                                                <td><input data-testid={`import-row-${r._key}-oem`} className="bm-input" style={{ padding: "4px 8px", fontSize: 13 }} value={r.oem_number} onChange={(e) => updateRow(r._key, { oem_number: e.target.value })} /></td>
                                                <td><input className="bm-input" type="number" step="0.01" style={{ padding: "4px 8px", fontSize: 13 }} value={r.quantity} onChange={(e) => updateRow(r._key, { quantity: e.target.value })} /></td>
                                                <td><input className="bm-input" type="number" step="0.01" style={{ padding: "4px 8px", fontSize: 13 }} value={r.unit_amount} onChange={(e) => updateRow(r._key, { unit_amount: e.target.value })} /></td>
                                                <td><input className="bm-input" type="number" step="0.01" style={{ padding: "4px 8px", fontSize: 13 }} value={r.line_total} onChange={(e) => updateRow(r._key, { line_total: e.target.value })} /></td>
                                                <td>
                                                    <button data-testid={`import-row-${r._key}-delete`} className="bm-btn bm-btn-danger" style={{ padding: "4px 6px" }} onClick={() => removeRow(r._key)} aria-label="Delete row">
                                                        <Trash2 size={12} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {/* Mobile view — cards (one row per part) */}
                            <div className="bm-import-cards">
                                {rows.length === 0 && (
                                    <div style={{ textAlign: "center", padding: 22, color: "var(--bm-text-muted)", fontSize: 13 }}>No line items found.</div>
                                )}
                                {rows.map((r) => (
                                    <div key={r._key} data-testid={`import-row-${r._key}-mobile`} style={{ padding: 12, borderBottom: "1px solid var(--bm-border-soft)", opacity: r._enabled ? 1 : 0.45 }}>
                                        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                                            <input type="checkbox" checked={r._enabled} onChange={(e) => updateRow(r._key, { _enabled: e.target.checked })} style={{ marginTop: 6 }} />
                                            <input className="bm-input" style={{ flex: 1, padding: "6px 8px", fontSize: 13, fontWeight: 600 }} placeholder="Description" value={r.description} onChange={(e) => updateRow(r._key, { description: e.target.value })} />
                                            <button data-testid={`import-row-${r._key}-delete-mobile`} className="bm-btn bm-btn-danger" style={{ padding: "6px 8px" }} onClick={() => removeRow(r._key)} aria-label="Delete row">
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                            <div>
                                                <div style={{ fontSize: 10, color: "var(--bm-text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>OEM #</div>
                                                <input className="bm-input" style={{ padding: "4px 8px", fontSize: 13 }} value={r.oem_number} onChange={(e) => updateRow(r._key, { oem_number: e.target.value })} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 10, color: "var(--bm-text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Qty</div>
                                                <input className="bm-input" type="number" step="0.01" style={{ padding: "4px 8px", fontSize: 13 }} value={r.quantity} onChange={(e) => updateRow(r._key, { quantity: e.target.value })} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 10, color: "var(--bm-text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Unit ({receipt.currency || defaultCurrency})</div>
                                                <input className="bm-input" type="number" step="0.01" style={{ padding: "4px 8px", fontSize: 13 }} value={r.unit_amount} onChange={(e) => updateRow(r._key, { unit_amount: e.target.value })} />
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 10, color: "var(--bm-text-dim)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Line ({receipt.currency || defaultCurrency})</div>
                                                <input className="bm-input" type="number" step="0.01" style={{ padding: "4px 8px", fontSize: 13 }} value={r.line_total} onChange={(e) => updateRow(r._key, { line_total: e.target.value })} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {hasCostsTab && (
                            <label data-testid="import-create-cost-entry" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--bm-text-muted)", marginBottom: 14 }}>
                                <input type="checkbox" checked={createCostEntry} onChange={(e) => setCreateCostEntry(e.target.checked)} />
                                Also file the receipt as a Costs entry ({receipt.total ? `${receipt.total} ${receipt.currency || defaultCurrency}` : "no total"})
                                {dataUri && filename && <span style={{ marginLeft: 6, color: "var(--bm-text-dim)", display: "inline-flex", alignItems: "center", gap: 4 }}><FileText size={12} />{filename}</span>}
                            </label>
                        )}

                        {err && <div data-testid="import-parts-err" style={{ color: "var(--bm-bad)", marginBottom: 12, fontSize: 13 }}>{err}</div>}

                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                            <button data-testid="import-cancel-btn" type="button" className="bm-btn bm-btn-ghost" onClick={onClose}>Cancel</button>
                            <button
                                data-testid="import-save-btn"
                                type="button"
                                className="bm-btn bm-btn-gold"
                                onClick={save}
                                disabled={enabledCount === 0}
                            >
                                <Save size={14} /> Add {enabledCount} part{enabledCount === 1 ? "" : "s"}
                            </button>
                        </div>
                    </div>
                )}

                {/* SAVING */}
                {stage === STAGES.SAVING && (
                    <div data-testid="import-stage-saving" style={{ padding: "40px 20px", textAlign: "center", color: "var(--bm-text-muted)" }}>
                        <Loader2 size={28} color="var(--bm-gold)" style={{ animation: "spin 1.4s linear infinite", marginBottom: 14 }} />
                        <div style={{ fontSize: 14 }}>Adding parts to your catalogue…</div>
                    </div>
                )}
            </div>
        </div>
    );
}
